import type { PosDatabase } from '@core/db/db'
import { META_ACTIVE_SHIFT_ID } from '@core/db/schema'

/**
 * Максимальный возраст открытой смены — 24 часа. Дольше смена не живёт:
 * «забытую» смену закрываем автоматически, иначе она блокирует зону.
 */
export const SHIFT_MAX_AGE_MS = 24 * 60 * 60 * 1000

/** За сколько до таймаута начинать предупреждать кассира (23 ч). */
export const SHIFT_WARN_AGE_MS = 23 * 60 * 60 * 1000

/**
 * Авто-закрытие «забытой» открытой смены по таймауту 24 ч.
 *
 * В Z-отчёт пишется признак `reason: 'timeout'`; активная смена в meta
 * снимается, чтобы касса предложила открыть новую. Вынесено в отдельный модуль,
 * чтобы и ShiftService, и SyncService (syncAll) могли вызывать проверку без
 * циклических импортов между сервисами.
 *
 * @returns номер авто-закрытой смены или null, если закрывать нечего.
 */
export async function autoCloseStaleShift(db: PosDatabase): Promise<number | null> {
  const active = await db.shifts.where('status').equals('open').first()
  if (!active || active.id == null) return null
  if (Date.now() - active.openedAt <= SHIFT_MAX_AGE_MS) return null

  // 65.14: смена с hold-чеками не закрывается автоматически — иначе hold
  // останется «сиротой», а Z зафиксирует неполный результат.
  const held = await db.receipts.where('status').equals('marking_review').count()
  const nonFiscal = await db.receipts.where('status').equals('non_fiscal').toArray()
  const pendingReplacements = nonFiscal.filter(
    (r) =>
      !r.trash &&
      (r.replacementState === 'awaiting_verification' ||
        r.replacementState === 'awaiting_print' ||
        r.replacementState === 'processing' ||
        r.replacementState === 'failed')
  ).length
  if (held > 0 || pendingReplacements > 0) {
    console.warn(
      `[ShiftService] Смена №${active.shiftNumber} не авто-закрыта: ` +
        `${held} hold-чек(ов), ${pendingReplacements} замен(ы) non_fiscal → fiscal`
    )
    return null
  }

  const closedAt = Date.now()
  const summary = {
    shift: { ...active, closedAt, status: 'closed' },
    salesCount: active.salesCount,
    totalRevenue: active.totalSales,
    cashTotal: active.totalCash ?? 0,
    cardTotal: active.totalCard ?? 0,
    sbpTotal: active.totalSbp ?? 0,
    reason: 'timeout'
  }

  await db.shifts.update(active.id, {
    status: 'closed',
    closedAt,
    zReportData: JSON.stringify(summary)
  })
  await db.meta.delete(META_ACTIVE_SHIFT_ID)
  console.warn(`[ShiftService] Смена №${active.shiftNumber} авто-закрыта по таймауту 24 ч`)
  return active.shiftNumber
}